import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

function EditTestModal({ test, open, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [activeAttempts, setActiveAttempts] = useState(0);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_time: '',
    end_time: '',
    config: {
      duration: 30,
      negative_marking: 0,
      attempts_allowed: 1,
      shuffle_questions: false,
      shuffle_choices: false,
      show_results_immediately: true,
      pass_percentage: 40.0,
      proctoring_enabled: true,
      tab_switch_limit: 3,
      require_fullscreen: true
    },
    question_ids: []
  });

  const toLocalDatetime = (isoStr) => {
    if (!isoStr) return '';
    try {
      const d = new Date(isoStr);
      if (isNaN(d.getTime())) return '';
      const offset = d.getTimezoneOffset();
      const local = new Date(d.getTime() - offset * 60000);
      return local.toISOString().slice(0, 16);
    } catch { return ''; }
  };

  useEffect(() => {
    if (test && open) {
      setFormData({
        title: test.title || '',
        description: test.description || '',
        start_time: test.start_time || '',
        end_time: test.end_time || '',
        config: test.config || formData.config,
        question_ids: test.question_ids || []
      });
      checkActiveAttempts();
    }
  }, [test, open]);

  const checkActiveAttempts = async () => {
    try {
      const response = await api.get(`/attempts/test/${test.id}/all-attempts`);
      const active = response.data.filter(a => a.status === 'in_progress').length;
      setActiveAttempts(active);
    } catch (error) {
      console.error('Error checking attempts:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.put(`/tests/${test.id}`, formData);
      toast.success('Test updated successfully!');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating test:', error);
      toast.error(error.response?.data?.detail || 'Failed to update test');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Test</DialogTitle>
        </DialogHeader>

        {activeAttempts > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div className="text-sm text-amber-900">
              <p className="font-semibold">Warning: {activeAttempts} active attempt(s)</p>
              <p>Changes will only affect new attempts. Ongoing tests will continue with current settings.</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Test Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                data-testid="edit-test-title"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                data-testid="edit-test-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_time">Test Start Date & Time</Label>
                <Input
                  id="start_time"
                  type="datetime-local"
                  value={toLocalDatetime(formData.start_time)}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                  data-testid="edit-test-start-time"
                />
                <p className="text-xs text-gray-500">Leave empty for no start restriction</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_time">Test End Date & Time</Label>
                <Input
                  id="end_time"
                  type="datetime-local"
                  value={toLocalDatetime(formData.end_time)}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                  data-testid="edit-test-end-time"
                />
                <p className="text-xs text-gray-500">Leave empty for no end restriction</p>
              </div>
            </div>
          </div>

          {/* Test Configuration */}
          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold mb-4">Test Configuration</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Duration (minutes)</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.config.duration}
                  onChange={(e) => setFormData({
                    ...formData,
                    config: { ...formData.config, duration: parseInt(e.target.value) }
                  })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Pass Percentage (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.config.pass_percentage}
                  onChange={(e) => setFormData({
                    ...formData,
                    config: { ...formData.config, pass_percentage: parseFloat(e.target.value) }
                  })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Negative Marking</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.25"
                  value={formData.config.negative_marking}
                  onChange={(e) => setFormData({
                    ...formData,
                    config: { ...formData.config, negative_marking: parseFloat(e.target.value) }
                  })}
                />
              </div>

              <div className="space-y-2">
                <Label>Tab Switch Limit</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.config.tab_switch_limit}
                  onChange={(e) => setFormData({
                    ...formData,
                    config: { ...formData.config, tab_switch_limit: parseInt(e.target.value) }
                  })}
                />
              </div>
            </div>

            <div className="space-y-3 mt-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="shuffle_questions"
                  checked={formData.config.shuffle_questions}
                  onCheckedChange={(checked) => setFormData({
                    ...formData,
                    config: { ...formData.config, shuffle_questions: checked }
                  })}
                />
                <Label htmlFor="shuffle_questions" className="cursor-pointer">Shuffle Questions</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="proctoring_enabled"
                  checked={formData.config.proctoring_enabled}
                  onCheckedChange={(checked) => setFormData({
                    ...formData,
                    config: { ...formData.config, proctoring_enabled: checked }
                  })}
                />
                <Label htmlFor="proctoring_enabled" className="cursor-pointer">Enable Proctoring</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="show_results"
                  checked={formData.config.show_results_immediately}
                  onCheckedChange={(checked) => setFormData({
                    ...formData,
                    config: { ...formData.config, show_results_immediately: checked }
                  })}
                />
                <Label htmlFor="show_results" className="cursor-pointer">Show Results Immediately</Label>
              </div>
            </div>
          </div>

          {/* Question Count (read-only) */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Questions</h3>
              <span className="text-sm font-medium text-blue-700 bg-blue-50 px-3 py-1 rounded-full" data-testid="edit-test-question-count">
                {formData.question_ids.length} selected
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-1">To add or remove questions, use the Test Creation page.</p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="edit-test-submit">
              {loading ? 'Updating...' : 'Update Test'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default EditTestModal;
